import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Card, CardContent, CardMedia, LinearProgress, Stack, styled, Typography, } from "@mui/material";
import { getLayoutInfo } from "../LayoutInfo";
import { useEffect, useState, useCallback, useRef, forwardRef, } from "react";
import { authUrl, useCacheBusterStore } from "../util/UrlUtils";
import { useLogger } from "../logger";
import { computeScaleDownSize } from "../util/ImageUtils";
import { safeStringify } from "../util/SafeJson";
import { HLSVideoPlayer } from "./HLSVideoPlayer";
import { useProfileAuthStore } from "../store/ProfileStore";
const CardContentNoPadding = styled(CardContent)(`
  padding: 0;
  &:last-child {
    padding-bottom: 0;
  }
`);
const LOGPREFIX = "ContentCard: ";
// Helper function to determine if a URL needs authentication
const needsAuthentication = (url) => {
    if (!url)
        return false;
    // Static assets in /img/ don't need authentication
    if (url.startsWith("/img/") || url.startsWith("./img/")) {
        return false;
    }
    // Channel images (assuming they don't need authentication based on user feedback)
    if (url.includes("channelImage_")) {
        return false;
    }
    // Everything else needs authentication
    return true;
};
export const ContentCard = forwardRef((props, ref) => {
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const auth = useProfileAuthStore((state) => state);
    const [forceRender, setForceRender] = useState(cacheBusterStore.getCacheBusterNumber());
    const [mediaLoadError, setMediaLoadError] = useState(false);
    const [mediaUrl, setMediaUrl] = useState(props.mediaUrl);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [width, setWidth] = useState(0);
    const [showVideoControls, setShowVideoControls] = useState(false);
    const [mediaIsVideo, setMediaIsVideo] = useState(props.mediaIsVideo ? props.mediaIsVideo : false);
    const [isPostView, setIsPostView] = useState(false);
    const [videoFullscreenRequested, setVideoFullscreenRequested] = useState(false);
    const [userExitedFullscreen, setUserExitedFullscreen] = useState(false);
    const layout = getLayoutInfo();
    const logger = useLogger((state) => state);
    // Create stable authenticated URLs that don't change when bearer token refreshes
    const [authenticatedMediaUrl, setAuthenticatedMediaUrl] = useState();
    const [authenticatedFallbackUrl, setAuthenticatedFallbackUrl] = useState();
    const [authenticatedThumbnailUrl, setAuthenticatedThumbnailUrl] = useState();
    // Create stable authenticated URLs when props change (only once per URL)
    useEffect(() => {
        logger.log(LOGPREFIX +
            "useEffect[mediaUrl]: triggered for keyStr=" +
            props.keyStr +
            ", mediaUrl=" +
            mediaUrl +
            ", authenticatedMediaUrl=" +
            authenticatedMediaUrl);
        if (mediaUrl && !authenticatedMediaUrl) {
            if (needsAuthentication(mediaUrl)) {
                logger.log(LOGPREFIX +
                    "useEffect[mediaUrl]: setting authenticated URL for " +
                    props.keyStr);
                setAuthenticatedMediaUrl(authUrl(mediaUrl, cacheBusterStore, auth));
            }
            else {
                logger.log(LOGPREFIX +
                    "useEffect[mediaUrl]: using direct URL (no auth needed) for " +
                    props.keyStr);
                setAuthenticatedMediaUrl(mediaUrl);
            }
        }
        else {
            logger.log(LOGPREFIX +
                "useEffect[mediaUrl]: skipping - no mediaUrl or already authenticated for " +
                props.keyStr);
        }
    }, [mediaUrl, authenticatedMediaUrl]);
    useEffect(() => {
        logger.log(LOGPREFIX +
            "useEffect[fallbackUrl]: triggered for keyStr=" +
            props.keyStr +
            ", fallbackUrl=" +
            props.mediaFallbackUrl +
            ", authenticatedFallbackUrl=" +
            authenticatedFallbackUrl);
        if (props.mediaFallbackUrl && !authenticatedFallbackUrl) {
            if (needsAuthentication(props.mediaFallbackUrl)) {
                logger.log(LOGPREFIX +
                    "useEffect[fallbackUrl]: setting authenticated fallback URL for " +
                    props.keyStr);
                setAuthenticatedFallbackUrl(authUrl(props.mediaFallbackUrl, cacheBusterStore, auth));
            }
            else {
                logger.log(LOGPREFIX +
                    "useEffect[fallbackUrl]: using direct fallback URL (no auth needed) for " +
                    props.keyStr);
                setAuthenticatedFallbackUrl(props.mediaFallbackUrl);
            }
        }
        else {
            logger.log(LOGPREFIX +
                "useEffect[fallbackUrl]: skipping - no fallbackUrl or already authenticated for " +
                props.keyStr);
        }
    }, [props.mediaFallbackUrl, authenticatedFallbackUrl]);
    useEffect(() => {
        logger.log(LOGPREFIX +
            "useEffect[thumbnailUrl]: triggered for keyStr=" +
            props.keyStr +
            ", thumbnailUrl=" +
            props.mediaThumbnailUrl +
            ", authenticatedThumbnailUrl=" +
            authenticatedThumbnailUrl);
        if (props.mediaThumbnailUrl && !authenticatedThumbnailUrl) {
            if (needsAuthentication(props.mediaThumbnailUrl)) {
                logger.log(LOGPREFIX +
                    "useEffect[thumbnailUrl]: setting authenticated thumbnail URL for " +
                    props.keyStr);
                setAuthenticatedThumbnailUrl(authUrl(props.mediaThumbnailUrl, cacheBusterStore, auth));
            }
            else {
                logger.log(LOGPREFIX +
                    "useEffect[thumbnailUrl]: using direct thumbnail URL (no auth needed) for " +
                    props.keyStr);
                setAuthenticatedThumbnailUrl(props.mediaThumbnailUrl);
            }
        }
        else {
            logger.log(LOGPREFIX +
                "useEffect[thumbnailUrl]: skipping - no thumbnailUrl or already authenticated for " +
                props.keyStr);
        }
    }, [props.mediaThumbnailUrl, authenticatedThumbnailUrl]);
    // Detect if we're on the PostView page
    useEffect(() => {
        const isOnPostView = window.location.pathname.includes("PostView") ||
            window.location.href.includes("PostView");
        setIsPostView(isOnPostView);
        // If we're on PostView and have a video, show controls by default
        if (isOnPostView && mediaIsVideo) {
            setShowVideoControls(true);
            logger.log(LOGPREFIX + "PostView detected with video - enabling controls");
        }
        // Note: Fullscreen event listeners are now handled in the handleVideoRef callback
        // to avoid duplicate listeners and ensure proper cleanup
    }, [mediaIsVideo]);
    const cleanupRef = useRef(null);
    useEffect(() => {
        if (mediaUrl && mediaIsVideo) {
            if (mediaUrl === props.mediaFallbackUrl) {
                logger.log(LOGPREFIX +
                    "Will render fallback image for " +
                    props.keyStr +
                    " with mediaUrl: " +
                    mediaUrl);
            }
            else {
                logger.log(LOGPREFIX +
                    "Will render HLSVideoPlayer for " +
                    props.keyStr +
                    " with mediaUrl: " +
                    mediaUrl);
            }
        }
    }, [mediaUrl, mediaIsVideo, props.mediaFallbackUrl, props.keyStr]);
    useEffect(() => {
        if (mediaLoadError) {
            // Only the true transition is worth a record; logging the false case
            // put the word "Error" into every healthy card render, which the
            // daily lexical failure scan counted as a failure.
            logger.log(LOGPREFIX + "mediaLoadError changed: true for " + props.keyStr);
        }
        if (mediaLoadError) {
            logger.log(LOGPREFIX +
                "Current mediaUrl: " +
                mediaUrl +
                ", fallback: " +
                props.mediaFallbackUrl);
            if (mediaUrl != props.mediaFallbackUrl && props.mediaFallbackUrl) {
                logger.log(LOGPREFIX +
                    "Using fallback Media for Content Card - " +
                    props.keyStr);
                setMediaIsVideo(false); // Fallback is always an image
                setMediaUrl(props.mediaFallbackUrl);
                // Reset the authenticated URL so it will be recreated for the fallback
                setAuthenticatedMediaUrl(undefined);
                setMediaLoadError(false);
                setForceRender(forceRender + 1);
            }
            else {
                logger.log(LOGPREFIX +
                    "Already using fallback or no fallback available, keeping current mediaUrl");
                // Don't set mediaUrl to undefined - keep the fallback URL even if it fails
                // This ensures the image element is still rendered (may show broken image icon)
            }
        }
        if (props.onMediaLoadError) {
            props.onMediaLoadError();
        }
        return () => { };
    }, [mediaLoadError]);
    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [setWidth]);
    // Update internal mediaUrl state when props.mediaUrl changes
    useEffect(() => {
        logger.log(LOGPREFIX + "props.mediaUrl changed: " + props.mediaUrl);
        setMediaUrl(props.mediaUrl);
    }, [props.mediaUrl]);
    useEffect(() => {
        logger.log(LOGPREFIX + "useEffect mount - mediaUrl = " + mediaUrl);
        if (mediaUrl && !mediaUrl.includes("img/")) {
            setMediaLoading(true);
        }
        return () => {
            logger.log(LOGPREFIX + "useEffect unmount - mediaUrl");
        };
    }, [mediaUrl]);
    // Handle video play/pause based on visibility
    const handleVideoRef = useCallback((element) => {
        // Clean up previous observer if it exists
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        if (element && mediaIsVideo) {
            // Set video properties for autoplay
            element.muted = true; // Required for autoplay in most browsers
            element.loop = true; // Loop the video
            element.playsInline = true; // Better mobile support
            // If we're on PostView, request fullscreen when video is ready (but not if user manually exited or during emoji interactions)
            if (isPostView &&
                !videoFullscreenRequested &&
                !userExitedFullscreen &&
                !props.emojiInteractionActive) {
                let fullscreenEventListenersAdded = false;
                const requestFullscreen = () => {
                    // Add event listeners for fullscreen changes before requesting fullscreen
                    const handleFullscreenChange = () => {
                        const doc = document;
                        const isCurrentlyFullscreen = !!(document.fullscreenElement ||
                            doc.webkitFullscreenElement ||
                            doc.mozFullScreenElement ||
                            doc.msFullscreenElement);
                        if (!isCurrentlyFullscreen) {
                            logger.log(LOGPREFIX + "Fullscreen exited via native controls");
                            setVideoFullscreenRequested(false);
                            setUserExitedFullscreen(true); // Mark that user manually exited
                            // Clean up event listeners when exiting fullscreen
                            if (fullscreenEventListenersAdded) {
                                document.removeEventListener("fullscreenchange", handleFullscreenChange);
                                document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
                                document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
                                document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
                                fullscreenEventListenersAdded = false;
                            }
                            // Optionally pause video when exiting fullscreen
                            if (element && !element.paused) {
                                element.pause();
                            }
                        }
                        else {
                            logger.log(LOGPREFIX + "Entered fullscreen mode");
                        }
                    };
                    // Only add event listeners if not already added
                    if (!fullscreenEventListenersAdded) {
                        document.addEventListener("fullscreenchange", handleFullscreenChange);
                        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
                        document.addEventListener("mozfullscreenchange", handleFullscreenChange);
                        document.addEventListener("MSFullscreenChange", handleFullscreenChange);
                        fullscreenEventListenersAdded = true;
                    }
                    // Request fullscreen with proper error handling
                    if (element.requestFullscreen) {
                        element
                            .requestFullscreen()
                            .then(() => {
                            logger.log(LOGPREFIX + "Video entered fullscreen successfully");
                            setVideoFullscreenRequested(true);
                        })
                            .catch((error) => {
                            logger.log(LOGPREFIX + "Fullscreen request failed: " + error);
                            // Clean up event listeners if fullscreen failed
                            if (fullscreenEventListenersAdded) {
                                document.removeEventListener("fullscreenchange", handleFullscreenChange);
                                document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
                                document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
                                document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
                                fullscreenEventListenersAdded = false;
                            }
                        });
                    }
                    else if ("webkitRequestFullscreen" in element) {
                        // Safari support
                        try {
                            element.webkitRequestFullscreen();
                            setVideoFullscreenRequested(true);
                        }
                        catch (error) {
                            logger.log(LOGPREFIX + "WebKit fullscreen request failed: " + error);
                        }
                    }
                    else if ("mozRequestFullScreen" in element) {
                        // Firefox support
                        try {
                            element.mozRequestFullScreen();
                            setVideoFullscreenRequested(true);
                        }
                        catch (error) {
                            logger.log(LOGPREFIX + "Mozilla fullscreen request failed: " + error);
                        }
                    }
                    else if ("msRequestFullscreen" in element) {
                        // IE/Edge support
                        try {
                            element.msRequestFullscreen();
                            setVideoFullscreenRequested(true);
                        }
                        catch (error) {
                            logger.log(LOGPREFIX + "MS fullscreen request failed: " + error);
                        }
                    }
                    else {
                        logger.log(LOGPREFIX + "Fullscreen API not supported");
                    }
                };
                // Wait for video to be ready before requesting fullscreen
                if (element.readyState >= 2) {
                    // Video has enough data to start playing
                    requestFullscreen();
                }
                else {
                    // Wait for video to load enough data
                    const handleCanPlay = () => {
                        requestFullscreen();
                        element.removeEventListener("canplay", handleCanPlay);
                    };
                    element.addEventListener("canplay", handleCanPlay);
                }
            }
            // Set up intersection observer for this video element
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Play video when it becomes visible
                        element.play().catch((error) => {
                            logger.log(LOGPREFIX + "Video autoplay failed: " + error);
                        });
                    }
                    else {
                        // Pause video when it goes out of view (but not in PostView fullscreen)
                        if (!isPostView || !document.fullscreenElement) {
                            element.pause();
                        }
                    }
                });
            }, {
                threshold: 0.5,
                rootMargin: "0px 0px -50px 0px", // Start playing slightly before fully visible
            });
            observer.observe(element);
            // Check if element is already intersecting when observer is first attached
            // This handles the case where videos are already visible when the page loads
            const checkInitialIntersection = () => {
                const rect = element.getBoundingClientRect();
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                // Calculate if element is intersecting using the same threshold (50%) and rootMargin
                const elementTop = rect.top;
                const elementBottom = rect.bottom;
                const elementLeft = rect.left;
                const elementRight = rect.right;
                // Apply rootMargin: "0px 0px -50px 0px" (reduces bottom of viewport by 50px)
                const adjustedViewportBottom = viewportHeight - 50;
                // Check if element is in viewport bounds
                const isInViewport = elementTop < adjustedViewportBottom &&
                    elementBottom > 0 &&
                    elementLeft < viewportWidth &&
                    elementRight > 0;
                if (isInViewport) {
                    // Calculate intersection ratio for threshold check
                    const visibleTop = Math.max(elementTop, 0);
                    const visibleBottom = Math.min(elementBottom, adjustedViewportBottom);
                    const visibleLeft = Math.max(elementLeft, 0);
                    const visibleRight = Math.min(elementRight, viewportWidth);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
                    const visibleArea = visibleHeight * visibleWidth;
                    const elementHeight = rect.height;
                    const elementWidth = rect.width;
                    const elementArea = elementHeight * elementWidth;
                    const intersectionRatio = elementArea > 0 ? visibleArea / elementArea : 0;
                    // Check if intersection ratio meets threshold (0.5)
                    if (intersectionRatio >= 0.5) {
                        logger.log(LOGPREFIX +
                            "Video already visible on page load - starting autoplay");
                        element.play().catch((error) => {
                            logger.log(LOGPREFIX + "Initial video autoplay failed: " + error);
                        });
                    }
                }
            };
            // Use requestAnimationFrame to ensure element is fully rendered before checking
            requestAnimationFrame(checkInitialIntersection);
            // Store cleanup function in ref
            cleanupRef.current = () => {
                observer.disconnect();
            };
        }
    }, [
        mediaIsVideo,
        isPostView,
        videoFullscreenRequested,
        userExitedFullscreen,
    ]);
    // Cleanup intersection observer on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, []);
    // Handle video click to show/hide controls
    const handleVideoClick = useCallback((event) => {
        if (mediaIsVideo) {
            // Toggle controls visibility
            setShowVideoControls(!showVideoControls);
            // If there's an onMediaClick handler, call it
            if (props.onMediaClick) {
                props.onMediaClick();
            }
            // Prevent event bubbling
            event.stopPropagation();
        }
    }, [mediaIsVideo, props.onMediaClick, showVideoControls]);
    const delayedClearMediaLoading = () => {
        setTimeout(() => {
            setMediaLoading(false);
        }, 1000);
    };
    let childComponentObj = props.childComponent;
    if (!childComponentObj && props.text) {
        childComponentObj = (_jsxs(Typography, Object.assign({ whiteSpace: "pre-line", variant: "body1", color: props.textColor, sx: { textAlign: "left", padding: "10px", wordBreak: "break-word" } }, { children: [mediaLoading && _jsx(LinearProgress, {}, "content-card-media-loading"), props.text ? props.text : ""] }), props.keyStr + "-typography"));
    }
    let mediaSx;
    if (props.onMediaClick) {
        mediaSx = {
            objectFit: "contain",
            cursor: "pointer",
        };
    }
    else {
        mediaSx = { objectFit: "contain" };
    }
    mediaSx.maxWidth = props.maxMediaWidth ? "" + props.maxMediaWidth : "100%";
    mediaSx.maxHeight = props.maxMediaHeight
        ? "" + props.maxMediaHeight
        : "100%";
    let maxWidth;
    if (props.maxWidth && width >= 1000) {
        maxWidth = props.maxWidth * width;
    }
    let titleLineHeight = "32px";
    if (props.titleAvatar) {
        titleLineHeight = "46px";
    }
    const elm = (_jsxs(Card, Object.assign({ ref: ref, "data-testid": props.keyStr, elevation: 10, sx: {
            backgroundColor: props.backgroundColor,
            padding: 0,
            margin: layout.cardMargin,
            alignContent: "center",
            textAlign: "center",
            width: "100%",
            maxWidth,
            objectFit: "contain",
        } }, { children: [props.header, mediaUrl && !props.overlayIcon && !mediaIsVideo && (_jsx(CardMedia, { component: "img", src: authenticatedMediaUrl || mediaUrl, sx: mediaSx, onClick: props.onMediaClick, onError: (err) => {
                    // Previously a try/catch around JSON.stringify, which survived
                    // cycles but silently fell back to the raw event ("[object
                    // Object]"). safeStringify keeps the detail.
                    const errMessage = safeStringify(err);
                    logger.log(LOGPREFIX + "CardMedia.onError: " + errMessage);
                    if (!props.skipMediaLoadErrorLogs) {
                        // Use warn instead of error when fallback exists (no server flush)
                        const hasFallback = props.mediaFallbackUrl || authenticatedFallbackUrl;
                        if (hasFallback) {
                            logger.warn(LOGPREFIX +
                                "CardMedia.onError-0: " +
                                props.keyStr +
                                " " +
                                mediaUrl +
                                " " +
                                err);
                        }
                        else {
                            logger.error(LOGPREFIX +
                                "CardMedia.onError-0: " +
                                props.keyStr +
                                " " +
                                mediaUrl +
                                " " +
                                err);
                        }
                    }
                    else {
                        logger.log(LOGPREFIX + "CardMedia.onError-0: " + mediaUrl + " " + err);
                    }
                    setMediaLoadError(true);
                    delayedClearMediaLoading();
                }, onLoad: (event) => {
                    logger.log(LOGPREFIX + "CardMedia.onLoad: " + mediaUrl);
                    if (event.target instanceof HTMLImageElement) {
                        logger.log(LOGPREFIX + "CardMedia.onLoad - element is image");
                        const img = event.target;
                        let maxImgWidth;
                        let maxImgHeight;
                        if (props.maxMediaHeight != undefined) {
                            maxImgHeight = props.maxMediaHeight;
                        }
                        else {
                            maxImgHeight = window.innerHeight - 10;
                        }
                        if (props.maxMediaWidth != undefined) {
                            maxImgWidth = props.maxMediaWidth;
                        }
                        else {
                            maxImgWidth = window.innerWidth - 10;
                        }
                        const newSizes = computeScaleDownSize(img.width, img.height, maxImgWidth, maxImgHeight);
                        if (img.height > newSizes.newHeight) {
                            img.width = newSizes.newWidth;
                            img.height = newSizes.newHeight;
                        }
                    }
                    else {
                        logger.log(LOGPREFIX + "ContentCard.onLoad - element = " + event.target);
                    }
                    delayedClearMediaLoading();
                    if (props.onMediaLoad) {
                        props.onMediaLoad();
                    }
                } }, props.keyStr +
                "-card-subelm-media-" +
                mediaUrl +
                "-" +
                forceRender)), mediaUrl &&
                !props.overlayIcon &&
                mediaIsVideo &&
                mediaUrl !== props.mediaFallbackUrl && (_jsx("div", Object.assign({ style: { width: "100%", position: "relative" } }, { children: _jsx(HLSVideoPlayer, { src: authenticatedMediaUrl || mediaUrl, controls: showVideoControls || isPostView, autoplay: false, muted: true, loop: true, playsInline: true, preload: "metadata", style: {
                        objectFit: "contain",
                        maxWidth: props.maxMediaWidth
                            ? `${props.maxMediaWidth}px`
                            : "100%",
                        maxHeight: props.maxMediaHeight
                            ? `${props.maxMediaHeight}px`
                            : "100%",
                        width: "100%",
                        height: "auto",
                        cursor: props.onMediaClick ? "pointer" : "default",
                    }, onClick: handleVideoClick, onError: (err) => {
                        // safeStringify, not JSON.stringify: `err` is a DOM Event or
                        // an hls.js ErrorData, both cyclic. The raw call threw, and
                        // because it threw here the two recovery calls below never
                        // ran — a failed video kept its loading spinner forever.
                        if (!props.skipMediaLoadErrorLogs) {
                            logger.error(LOGPREFIX +
                                "HLSVideoPlayer.onError: " +
                                props.keyStr +
                                " " +
                                mediaUrl +
                                " " +
                                safeStringify(err));
                        }
                        else {
                            logger.log(LOGPREFIX +
                                "HLSVideoPlayer.onError: " +
                                mediaUrl +
                                " " +
                                safeStringify(err));
                        }
                        setMediaLoadError(true);
                        delayedClearMediaLoading();
                    }, onLoadedMetadata: () => {
                        logger.log(LOGPREFIX + "HLSVideoPlayer.onLoadedMetadata: " + mediaUrl);
                        delayedClearMediaLoading();
                        if (props.onMediaLoad) {
                            props.onMediaLoad();
                        }
                    }, onIntersectionChange: handleVideoRef, cannotPlayImageUrl: authenticatedFallbackUrl, poster: authenticatedThumbnailUrl }, props.keyStr +
                    "-card-subelm-video-" +
                    mediaUrl +
                    "-" +
                    forceRender) }), props.keyStr +
                "-card-subelm-video-container-" +
                mediaUrl +
                "-" +
                forceRender)), mediaUrl &&
                !props.overlayIcon &&
                mediaIsVideo &&
                mediaUrl === props.mediaFallbackUrl && (_jsx(CardMedia, { component: "img", src: authenticatedFallbackUrl || mediaUrl, sx: mediaSx, onClick: props.onMediaClick, onError: (err) => {
                    logger.log(LOGPREFIX + "CardMedia.onError: " + err);
                    if (!props.skipMediaLoadErrorLogs) {
                        logger.error(LOGPREFIX +
                            "CardMedia.onError-fallback: " +
                            props.keyStr +
                            " " +
                            mediaUrl +
                            " " +
                            err);
                    }
                    else {
                        logger.log(LOGPREFIX +
                            "CardMedia.onError-fallback: " +
                            mediaUrl +
                            " " +
                            err);
                    }
                    // Don't set mediaLoadError again to avoid infinite loop
                    delayedClearMediaLoading();
                }, onLoad: () => {
                    logger.log(LOGPREFIX + "CardMedia.onLoad-fallback: " + mediaUrl);
                    delayedClearMediaLoading();
                    if (props.onMediaLoad) {
                        props.onMediaLoad();
                    }
                } }, props.keyStr +
                "-card-subelm-fallback-" +
                mediaUrl +
                "-" +
                forceRender)), mediaUrl && props.overlayIcon && (_jsxs(CardMedia, Object.assign({ sx: mediaSx, onClick: props.onMediaClick }, { children: [_jsx("img", { src: authenticatedMediaUrl || mediaUrl, width: "100%", style: {
                            maxHeight: "100%",
                            maxWidth: "100%",
                            objectFit: "contain",
                        }, onError: (err) => {
                            logger.error(LOGPREFIX + "CardMedia.onError-1: " + mediaUrl + " " + err);
                            setMediaLoadError(true);
                            delayedClearMediaLoading();
                        }, onLoad: () => {
                            logger.log(LOGPREFIX + "CardMedia.onLoad: " + mediaUrl);
                            delayedClearMediaLoading();
                            if (props.onMediaLoad) {
                                props.onMediaLoad();
                            }
                        } }, props.keyStr + ".cardmedia.image." + mediaUrl + forceRender), _jsx("div", Object.assign({ style: {
                            textAlign: "right",
                            width: "99%",
                            paddingRight: "0.3em",
                            marginTop: "-50px",
                            paddingLeft: "auto",
                            paddingBottom: "5px",
                        } }, { children: props.overlayIcon }), props.keyStr + ".cardmedia.overlay.button.div." + forceRender)] }), props.keyStr + "-card-subelm-media-2-" + mediaUrl + forceRender)), _jsxs(CardContentNoPadding, { children: [props.title && (_jsxs(Stack, Object.assign({ direction: "row", width: props.titleAvatar ? 0.96 : 1.0, sx: {
                            justifyContent: "center",
                            marginLeft: "3px",
                            marginRight: "3px",
                            paddingLeft: 0,
                            paddingRight: 0,
                        } }, { children: [props.titleAvatar, _jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: {
                                    lineHeight: titleLineHeight,
                                    marginLeft: 0,
                                    marginRight: 0,
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                }, noWrap: true }, { children: props.title }), props.keyStr + "-card-content-subelm-typeography-0")] }))), childComponentObj] }, props.keyStr + "-card-content-subelm-" + mediaUrl + forceRender), props.actions] }), props.keyStr + "-card-subelm-" + mediaUrl + "-" + forceRender));
    return elm;
});
ContentCard.displayName = "ContentCard";
